import { useSelector } from 'react-redux';
import V2Title from '@/common/components/Feedback/V2Title';
import { Col, Form, Row, Spin, Tooltip, } from 'antd';
import { FC, Fragment, isValidElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getUserPageList } from '@/common/api/brief';
import V2Form from '@/common/components/Form/V2Form';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import { useMethods } from '@lhb/hook';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInputNumber from '@/common/components/Form/V2FormInputNumber/V2FormInputNumber';
import V2FormUpload from '@/common/components/Form/V2FormUpload/V2FormUpload';
import IconFont from '@/common/components/IconFont';
import moment from 'moment';
import { editFieldDefaultConfig, imagePropertyNames, ShopEditType } from '../ts-config';
import { isArray, isNotEmpty, isNotEmptyAny } from '@lhb/func';
import FormFranchisee from '@/common/components/business/ExpandStore/TaskCreateDrawer/components/FormFranchisee';
// import { ShopDetailAdditionalItems } from '../../shopDetail/ts-config';
import styles from './index.module.less';
import MapAddressChangeModal from './MapAddressChangeModal';
import { StoreStatus, RentalModel } from '@/common/components/business/ExpandStore/ts-config';
import UserInfoContext from '@/layout/context';
import { globalFranchiseeAlias } from '@/common/utils/ways';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import useUpdateEffect from '@/common/hook/updateEffect/useUpdateEffect';
import { ShopStatus } from '../../shop/ts-config';
import { getShopMatchModelClusterScore } from '@/common/api/expandStore/shop';
import FormSetName from '@/common/components/Form/FormSetName/FormSetName';
import FormSearchUser from '@/common/components/Form/FormSearchUser';

/** 需要特殊处理的编辑项 */
export const UniqueEditCode = ['franchiseeName', 'matchModelClusterScore',
  'managerName', // 开发人员
  'investmentManagerName', // 招商人员
];

/**
 * @Description 门店编辑表单
 */
const EditMain: FC<{
  form,
  detail,
  style,
  editType,
  /** 选址地图创建门店时，传入的点位信息 */
  mapPoiInfo,
  setAddressInfo
}> = ({
  form,
  detail,
  style,
  editType,
  mapPoiInfo,
  setAddressInfo,
}) => {
  const [configs, setConfigs] = useState<any>([]);
  const rentModeValue = Form.useWatch('rentMode', form);
  const franchiseeRef: any = useRef();
  const managerRef: any = useRef(); // 开发人员选人组件
  const [openMap, setOpenMap] = useState(false); // 打开调整位置弹窗

  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息
  const franchiseeAlias = globalFranchiseeAlias(globalAliasItemVOs);
  const [uniqueName, setUniqueName] = useState<string>(`${franchiseeAlias}唯一标识`); // 加盟商唯一字段名称
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const isSjtb = !!tenantCheckData?.isSjtb;

  const autoPopupedAddressModalRef: any = useRef(false); // 是否已自动弹出过地址选择框

  const isTypeCreate = editType === ShopEditType.Create;
  const isTypeMapCreate = editType === ShopEditType.MapCreate;
  const isTypeSign = editType === ShopEditType.Sign;
  // 三津汤包租户的编辑门店时禁用字段的特殊逻辑 https://confluence.lanhanba.com/pages/viewpage.action?pageId=172105146
  const isSjtbDisabled = isSjtb && editType === ShopEditType.Edit;
  const [loadingModelClusterScore, setLoadingModelClusterScore] = useState(false);

  const showChangeAddressHandle = useMemo(() => {
    if (isTypeMapCreate && isNotEmptyAny(mapPoiInfo)) return false;
    return true;
  }, [isTypeMapCreate, mapPoiInfo]);

  useEffect(() => {
    detail?.uniqueName && setUniqueName(detail.uniqueName);
  }, [detail]);

  // 选址地图创建门店时，传入点位信息，更新“所属商圈评分”
  useEffect(() => {
    // 更新“所属商圈评分”
    methods.updateShopMatchModelClusterScore({
      cityId: mapPoiInfo?.cityId,
      lng: mapPoiInfo?.lng,
      lat: mapPoiInfo?.lat,
    });
  }, [mapPoiInfo]);

  useEffect(() => {
    methods.setHiddenField(configs, rentModeValue);
    isNotEmptyAny(configs) && setConfigs([...configs]);
  }, [rentModeValue]);

  useEffect(() => {
    // 创建场景，找到地址选择字段，自动弹出一次地址选择框
    const addressField = configs.find(item => item.code === 'address');
    if ((isTypeCreate) && !autoPopupedAddressModalRef.current && addressField) {
      methods.changeAddress();
      autoPopupedAddressModalRef.current = true;
    }
  }, [configs]);

  /**
    * @description 是否摩可多租户
  */
  const isMKD = useMemo(() => {
    return !!tenantCheckData?.isMkd;
  }, [tenantCheckData]);

  /**
   * @description 是否已勾选门店表头字段-经纬度
   * @return
   */
  const hasCheckedPositionConfig = useMemo(() => {
    return isArray(configs) && !!configs.find((itm: any) => itm.code === 'position' && !!itm.checked);
  }, [configs]);

  useEffect(() => {
    methods.onConfigsChange(detail?.configs);
  }, [detail?.configs]);

  useUpdateEffect(() => {
    // 地图创建场景，找到地址选择字段
    const addressField = configs.find(item => item.code === 'address');
    if (isTypeMapCreate && addressField) {
      if (isNotEmptyAny(mapPoiInfo)) {
        form.setFieldValue('provinceName', mapPoiInfo.provinceName);
        form.setFieldValue('cityName', mapPoiInfo.cityName);
        form.setFieldValue('districtName', mapPoiInfo.districtName);
        form.setFieldValue('address', mapPoiInfo.address);
        form.setFieldValue('position', `${mapPoiInfo.lng},${mapPoiInfo.lat}`);
        form.validateFields(['address', 'position']);
      }
    };
  }, [configs, mapPoiInfo]);

  useEffect(() => {
    if (franchiseeRef.current) {
      const { franchiseeName, uniqueId, franchiseeId } = detail;
      if (franchiseeId) {
        franchiseeRef?.current?.addOption({ name: franchiseeName, id: franchiseeId, uniqueId: uniqueId });
      }
    }
  }, [franchiseeRef?.current]);

  const methods = useMethods({
    onConfigsChange(_configs: any[]) {
      if (!isArray(_configs)) return;

      const fieldsValue = {};
      // 特定字段隐藏，比如创建时隐藏数据来源字段
      const showFields = _configs?.filter(item => item.checked && item.code !== 'contractRemainingDays' && !((isTypeCreate || isTypeMapCreate) && item.field === 'origin'));
      /**
     * 过滤列表不显示的信息，以及合同到期天数
     */
      const result = showFields?.map(item => {

        if (item.field === 'status' && +detail[item.field] === StoreStatus.Opened) { // 如果是门店状态-开业中
          const reulst = showFields.find(itm => itm.field === 'openDate');
          reulst && (reulst.required = true);
        }
        // 地址和经纬度不可编辑
        if (item.field === 'address' || item.field === 'position') {
          item.disabled = true;
        }
        if (item.controlType === 'area') {
          fieldsValue[item.code] = detail[item.code];
        } else if (item.controlType !== 'datepicker') {
          fieldsValue[item.field] = detail[item.field];
          if (item.controlType === 'dropDownRadio') {
            // 创建门店  || 在选址地图中创建门店时
            if ((isTypeCreate || isTypeMapCreate) && isArray(item.selectionList) && item.selectionList.length) {
            // 默认值
              const defaultItem = item.selectionList.find(s => s.isDefault === 1);
              fieldsValue[item.field] = defaultItem ? defaultItem.value : null;
            }
          }
        } else {
          fieldsValue[item.field] = detail[item.field] ? moment(detail[item.field]) : undefined;
        }

        if (item.code === 'number') { // 如果是门店编号
          item.rules = item.required ? ([{ required: true, message: `请输入${!item.name || isValidElement(item.name) ? '' : item.name}` }]) : [];
          item.name = <div>
            <Tooltip title={`保证${item.name}唯一`}>
              <span>{item.name}</span>
              <IconFont style={{ color: '#CCCCCD', marginLeft: '3px', marginRight: '5px' }} iconHref='pc-common-icon-ic_info' />
            </Tooltip>
          </div>;
        }

        if (item.code === 'address') { // 如果是地址，需要特殊处理，否则提示红字带英文
          item.rules = item.required ? ([{ required: true, message: `请输入${item.name || ''}` }]) : [];
        }
        if (['contractEnd'].includes(item.code)) {
          item.formItemConfig = {
            // formItemConfig.rules 会覆盖日期组件根据 required 生成的默认必填规则。
            rules: [
              ...(item.required ? [{ required: true, message: `请选择${item.name || ''}` }] : []),
              { validator: methods.validateContractEnd }
            ],
            validateTrigger: ['onChange', 'onBlur', 'onSubmit']
          };
        }
        if (item.code === 'franchiseeName') { // 如果是加盟商
          const { uniqueId } = detail;
          fieldsValue['idCard'] = uniqueId;
        }

        editFieldDefaultConfig[item.field] && (item.config = editFieldDefaultConfig[item.field]);
        if (item.field === 'attachment') {
          if (isTypeSign) { // 签约场景
            item.config = {
              ...editFieldDefaultConfig[item.field],
              maxCount: 10,
              fileType: ['doc', 'docx', 'pdf', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'bmp']
            };
          }
          // 特定租户特殊处理，固定必填
          isMKD && (item.required = true);
        }
        // 特殊处理开发人员（https://confluence.lanhanba.com/pages/viewpage.action?pageId=172104928 开发人员由原本的字符串变成了选人的控件）
        if (item.field === 'managerName' && detail?.managerId && detail?.managerName) {
          fieldsValue['managerName'] = [{ value: detail.managerId, label: detail.managerName }];
        }
        // 特殊处理招商人员：详情配置的 field 为 investmentManagerId，但展示名称来自 investmentManagerName
        if (item.code === 'investmentManagerName') {
          fieldsValue['investmentManagerName'] = isNotEmpty(detail?.investmentManagerId)
            ? [{ value: detail.investmentManagerId, label: detail.investmentManagerName }]
            : [];
        }
        return item;

      }) || [];
      form?.setFieldsValue(fieldsValue);
      methods.setHiddenField(result, detail.rentMode);
      setConfigs(result);
    },
    /**
     * 设置隐藏字段
     * @param fields 显示fileds
     * @param currentValue 当前选中值，
     */
    setHiddenField(fields, currentValue = '') {
      if (!isNotEmptyAny(fields)) return;
      const fieldMap = (Array.isArray(fields) ? fields : []).reduce((result, item) => {
        result[item.field] = item;
        return result;
      }, {});

      const firstYearMonthRentField = fieldMap['firstYearMonthRent'];
      const secondYearMonthRentField = fieldMap['secondYearMonthRent'];
      const thirdYearMonthRentField = fieldMap['thirdYearMonthRent'];
      const brokerageField = fieldMap['brokerage'];

      // 已闭店显示时间和原因
      if (detail?.status !== ShopStatus.CLOSED) {
        fieldMap['closeDate'] && (fieldMap['closeDate'].hidden = true);
        fieldMap['closeReason'] && (fieldMap['closeReason'].hidden = true);
      }

      if (+currentValue === RentalModel.Reduce) { // 扣点模式
        firstYearMonthRentField && (firstYearMonthRentField.hidden = true);
        secondYearMonthRentField && (secondYearMonthRentField.hidden = true);
        thirdYearMonthRentField && (thirdYearMonthRentField.hidden = true);
        brokerageField && (brokerageField.hidden = false);
      } else if (+currentValue === RentalModel.High) { // 租金模式-取高模式
        firstYearMonthRentField && (firstYearMonthRentField.hidden = false);
        secondYearMonthRentField && (secondYearMonthRentField.hidden = false);
        thirdYearMonthRentField && (thirdYearMonthRentField.hidden = false);
        brokerageField && (brokerageField.hidden = false);
      } else if (!currentValue) { // 如果没有设置值
        firstYearMonthRentField && (firstYearMonthRentField.hidden = true);
        secondYearMonthRentField && (secondYearMonthRentField.hidden = true);
        thirdYearMonthRentField && (thirdYearMonthRentField.hidden = true);
        brokerageField && (brokerageField.hidden = true);
      } else { // 其他模式
        firstYearMonthRentField && (firstYearMonthRentField.hidden = false);
        secondYearMonthRentField && (secondYearMonthRentField.hidden = false);
        thirdYearMonthRentField && (thirdYearMonthRentField.hidden = false);
        brokerageField && (brokerageField.hidden = true);
      }

      setConfigs(fields);
    },
    /**
     * 获取筛选项的值
     * @param options
     * @returns
     */
    getSelectOptions(options) {
      return options?.map(item => {
        return {
          label: item.name,
          value: item.value,
        };
      });
    },
    /**
     * 开发人员选择变更
     * 同步写入隐藏的 managerId 字段，并把选中人员的手机号回填到 managerMobile
     * @param value labelInValue 格式的选中项，单选时为对象，清空时为 undefined
     */
    handleManagerChange(value: any) {
      const selected = Array.isArray(value) ? value[0] : value;
      const managerId = selected?.value;
      // 通过 ref 取选中项的完整原始数据，拿手机号
      const item = isNotEmpty(managerId) ? managerRef?.current?.getItem(managerId) : null;
      const mobile = Array.isArray(item) ? item[0]?.mobile : item?.mobile;

      form.setFieldsValue({
        managerId: managerId ?? undefined,
        managerMobile: mobile || undefined,
      });
    },
    /** 招商人员选人组件沿用开发人员的单选数组格式。 */
    getInvestmentManagerFromEvent(value: any) {
      return isNotEmpty(value) ? (Array.isArray(value) ? value : [value]).map(item => ({ value: item.value, label: item.label })) : [];
    },
    getInvestmentManagerValueProps(value: any) {
      return { value: Array.isArray(value) && value.length ? value[0] : null };
    },
    handleFranchiseeChange(value) {
      const data = franchiseeRef?.current?.getItem(value);
      form.setFieldsValue({ idCard: data?.uniqueId || '-' });
      data?.uniqueName && (data.uniqueName !== uniqueName) && setUniqueName(data.uniqueName);
    },
    handleFranchiseeCreated(val: any) {
      // 新增加盟商后获取加盟商详情
      methods.getFranchiseeInfo(val);
    },

    getFranchiseeInfo(franId: number) {
      if (franId) {
        getFranchiseeDetail({ id: franId }).then((data) => {
          const { name, uniqueId, uniqueName: uniName } = data;

          const franchiseeField = configs.find(item => item.code === 'franchiseeName');
          const fieldsValue = { idCard: uniqueId };
          franchiseeField && (fieldsValue[franchiseeField.field] = franId);
          form.setFieldsValue(fieldsValue);

          uniName && (uniName !== uniqueName) && setUniqueName(uniName);

          franchiseeRef?.current?.addOption({ name, id: franId, uniqueId });
        });
      } else {
        form.setFieldsValue({ idCard: undefined });
      }
    },

    /** 选择地址 */
    changeAddress() {
      setOpenMap(true);
    },
    /** 选择地址回调 */
    changeAddressComplete(val) {
      setAddressInfo(val);

      // 更新“所属商圈评分”
      methods.updateShopMatchModelClusterScore({
        cityId: val?.cityId,
        lng: val?.lng,
        lat: val?.lat,
      });

    },
    /** 更新“所属商圈评分” */
    updateShopMatchModelClusterScore(params) {
      if (!params?.cityId || !params?.lng || !params?.lat) {
        return;
      }

      form.setFieldsValue({ matchModelClusterScore: null });

      setLoadingModelClusterScore(true);
      getShopMatchModelClusterScore({
        cityId: params?.cityId,
        lng: params?.lng,
        lat: params?.lat,
      }).then(res => {
        console.log('matchModelClusterScore', res);
        form.setFieldsValue({
          matchModelClusterScore: isNotEmptyAny(res) ? res : null,
        });
      }).finally(() => {
        setLoadingModelClusterScore(false);
      });
    },

    // 自定义校验函数
    validateContractEnd() {
      const start = form.getFieldValue('contractStart');
      const end = form.getFieldValue('contractEnd');
      if (end && start && end.isBefore(start)) {
        return Promise.reject(new Error('结束时间不得早于起始时间'));
      }
      return Promise.resolve();
    }
  });

  return (
    <>
      <V2Form form={form} className={'pt-16 pb-16 pl-40 pr-40'} style={{ ...style }}>
        <V2Title type='H2' text='基本信息' divider className='mb-16'/>
        <Row gutter={16}>
          {configs?.map(item => {
            return (<Fragment key={item.code}>
              {/* 加盟商字段 */}
              {item.code === 'franchiseeName' && !item?.hidden ? <>
                <Col span={8} key={item.code}>
                  <FormFranchisee
                    disabled={isSjtbDisabled || item.disabled}
                    label={item.name}
                    name={item.field}
                    formItemConfig={{
                      rules: [
                        ...(isArray(item.rules) ? item.rules : []),
                        ...(item.required ? [{ required: true, message: `请选择${item.name || ''}` }] : [])
                      ]
                    }}
                    config={item.config}
                    placeholder={`请输入${item.name}姓名`}
                    onCreated={methods.handleFranchiseeCreated}
                    onChange={methods.handleFranchiseeChange}
                    ref={franchiseeRef}/>
                </Col>
                <Col span={8}>
                  <V2FormInput label={uniqueName} disabled={true} name='idCard'/>
                </Col>
              </> : ''}

              { // 招商人员
                item.code === 'investmentManagerName' && !item?.hidden && <>
                  <Col span={8} key={item.code}>
                    <FormSearchUser
                      label={item.name}
                      name={item.code}
                      allowClear={true}
                      required={item.required}
                      disabled={isSjtbDisabled || item.disabled}
                      needCacheSelect
                      labelInValue
                      selectApi={getUserPageList}
                      formItemConfig={{
                        getValueFromEvent: methods.getInvestmentManagerFromEvent,
                        getValueProps: methods.getInvestmentManagerValueProps
                      }}
                    />
                  </Col>
                </>
              }
              { // 开发人员
                item.code === 'managerName' && !item?.hidden && <>
                  <Col span={8} key={item.code}>
                    <FormSearchUser
                      label={item.name}
                      name={item.field}
                      allowClear={true}
                      // rules={rules}
                      required={item.required}
                      disabled={isSjtbDisabled || item.disabled}
                      needCacheSelect
                      labelInValue
                      selectApi={getUserPageList}
                      ref={managerRef}
                      onChange={methods.handleManagerChange}
                      formItemConfig={{
                        /** 转换 onChange 的值为指定格式 https://ant.design/components/form-cn#formitem */
                        getValueFromEvent: (...[val]) => {
                          // 将选中项的 value 转换为数组（主要是单选情况）
                          return isNotEmpty(val) ? (Array.isArray(val) ? val : [val]).map(item => ({ value: item.value, label: item.label })) : [];
                        },
                        /** 转换传入的值为 [{ label, value }]，return 的格式一定是 { value: [{ value, label ] } 或 { value: { value, label } } */
                        getValueProps: (value: any): any => {
                          // 因为该组件保存的值始终为数组格式，所以这里处理单选情况下的值
                          const _value = Array.isArray(value) ? value : [];
                          return { value: isNotEmpty(_value) ? (_value.length > 0 ? _value[0] : null) : null };
                        }
                      }}

                    />
                  </Col>
                </>
              }
              {/* 输入框 */}
              {item.controlType === 'input' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}>
                <V2FormInput
                  // 开发人员手机号固定只读，值由开发人员选人组件带出
                  disabled={item.code === 'managerMobile' ? true : ((isSjtbDisabled && !['address'].includes(item.code)) || item.disabled)}
                  className={styles.formInputContent}
                  label={item.field === 'address' && hasCheckedPositionConfig && showChangeAddressHandle ? (
                    <div className={styles.addressLabel}>
                      <div>{ item.name }</div>
                      <div className={styles.labelRightBtn} onClick={methods.changeAddress}>调整定位</div>
                    </div>
                  ) : item.name}
                  name={item.field}
                  required={item.required}
                  rules={item.rules}
                  config={item.config}
                />
              </Col> : '' }
              {/* 多行输入框 */}
              {item.controlType === 'textarea' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormTextArea disabled={isSjtbDisabled || item.disabled} label={item.name} name={item.field} required={item.required} config={{ showCount: true, ...item.config }} /> </Col> : ''}
              {/* 下拉选择框 */}
              {item.controlType === 'dropDownRadio' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col
                span={8}
                key={item.code}
                // 开发人员id字段不展示，但仍需渲染以便往表单塞值
                style={item.code === 'managerId' ? { display: 'none' } : undefined}
              >
                <V2FormSelect
                  disabled={(isSjtbDisabled && !['channelTypeName', 'statusName', 'businessType'].includes(item.code)) || item.disabled}
                  label={item.name}
                  name={item.field}
                  required={item.required}
                  config={{
                    getPopupContainer: (node) => node.parentNode
                  }}
                  options={methods.getSelectOptions(item.selectionList)}
                />
              </Col>
                : ''}
              {/* 日期选择器 */}
              {item.controlType === 'datepicker' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormDatePicker disabled={isSjtbDisabled || item.disabled} label={item.name} name={item.field} required={item.required} formItemConfig={item.formItemConfig}/> </Col> : ''}
              {/* 数字输入框 */}
              {item.controlType === 'numberInput' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormInputNumber disabled={isSjtbDisabled || item.disabled} label={item.name} name={item.field} required={item.required} config={{ ...item?.config, addonAfter: item?.config?.unit, }} /> </Col> : ''}
              {/* 文件上传 */}
              {item.controlType === 'fileUpload' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormUpload promptFileAllSuccess label={item.name} name={item.field} required={item.required} config={{ ...item.config, disabled: isSjtbDisabled || item.disabled }} uploadType={imagePropertyNames.includes(item.code) ? 'image' : 'file'} /> </Col> : ''}
              {/* 省市区 */}
              {item.controlType === 'area' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormInput label={item.name} name={item.code} required={item.required} disabled={true} /> </Col> : ''}
              {item.controlType === 'other' && !item?.hidden ? <OtherItem key={item.code} item={item} loadingModelClusterScore={loadingModelClusterScore} /> : ''}
            </Fragment>);
          })}
        </Row>
      </V2Form>
      <MapAddressChangeModal
        open={openMap}
        setOpen={setOpenMap}
        form={form}
        setAddressInfo={methods.changeAddressComplete}
      />
    </>
  );
};

/**
 * @description 门店编辑表单-其他项组件
 * @demo
 */
const OtherItem:FC<{ item, loadingModelClusterScore }> = ({ item, loadingModelClusterScore }) => {

  /* state */

  /* hooks */

  /* methods */

  return (<>
    {item.code === 'matchModelClusterScore' && <MatchModelClusterScore item={item} loading={loadingModelClusterScore} />}
  </>);
};

/**
 * @description 门店编辑表单-其他项组件-所属商圈评分
 * @demo
 */
const MatchModelClusterScore:FC<{ item, loading }> = ({ item, loading }) => {

  /* state */

  /* hooks */

  /* methods */

  return (<Col span={8} key={item.code}>
    <Spin spinning={loading}>
      <FormSetName name={item.field} />
      <V2FormInputNumber
        disabled={true}
        className={styles.formInputContent}
        label={item.name}
        name={[item.field, 'clusterScore']}
        required={item.required}
        config={item.config}
        precision={1}
      />
    </Spin>
  </Col>);
};


export default EditMain;
